import HitRecord from "models/view-models/search/hit-record";
import { CollectionUtils, RecordUtils } from "@rsm-hcd/javascript-core";
import { ConversationMessage } from "models/interfaces/conversations/conversation-message";
import { MessageRole } from "models/enumerations/conversations/message-role";
import { Record } from "immutable";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import { isNumber } from "lodash";

const defaultValues: ConversationMessage = {
    citations: [],
    content: undefined,
    conversationId: undefined,
    chatFinishReason: undefined,
    id: undefined,
    index: undefined,
    messageId: undefined,
    parentId: undefined,
    prompt: undefined,
    feedback: undefined,
    role: MessageRole.User,
    status: ConversationMessageStatus.Completed,
    createdOn: undefined,
};

export default class ConversationMessageRecord
    extends Record(defaultValues)
    implements ConversationMessage
{
    // Do NOT set properties on immutable records due to babel and typescript transpilation issue
    // See https://github.com/facebook/create-react-app/issues/6506

    // -----------------------------------------------------------------------------------------
    // #region Constructor
    // -----------------------------------------------------------------------------------------

    constructor(params?: Partial<ConversationMessage>) {
        if (params == null) {
            params = Object.assign({}, defaultValues);
        }

        if (CollectionUtils.hasValues(params.citations)) {
            params.citations = RecordUtils.ensureRecords(
                params.citations,
                HitRecord
            );
        }

        params.role = parseRole(params.role);

        super(params);
    }

    // #endregion Constructor

    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    /**
     * Search the list of messages for the Tool message containing the citations for this message's parentId.
     */
    public getCitations(messages: ConversationMessageRecord[]): HitRecord[] {
        return (
            messages.find(
                (m) => m.role === MessageRole.Tool && m.id === this.parentId
            )?.citations ?? []
        );
    }

    /**
     * Merges new values into the record and returns a new instance.
     *
     * @param {Partial<ConversationMessage>} values
     */
    public with(
        values: Partial<ConversationMessage>
    ): ConversationMessageRecord {
        return new ConversationMessageRecord(
            Object.assign(this.toJS(), values)
        );
    }

    // #endregion Public Methods
}

function parseRole(role: unknown): MessageRole {
    if (isNumber(role)) {
        return role;
    }

    if (role === "User") {
        return MessageRole.User;
    }

    if (role === "System") {
        return MessageRole.System;
    }

    if (role === "Tool") {
        return MessageRole.Tool;
    }

    return MessageRole.Assistant;
}
